import { useCallback, useMemo } from "react";
import { useField } from "formik";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import type { SelectAllMatchingControl } from "@design-system/ViewTable/ViewTableCheckboxHeader";
import {
    resolveRowSelection,
    resolveSelectAllControl,
} from "./ruleTransactionSelection.logic";

interface SelectableRow {
    id: number;
}

interface UseRuleTransactionSelectionOptions<T extends SelectableRow> {
    flattenedTransactions: T[];
    isShowingApplied?: boolean;
    matchingTotal?: number;
    nonLockedIds: Set<number>;
    /**
     * Optional row-key resolver. When the table renders multiple rows per
     * logical transaction (e.g. split previews), we key selection state by
     * this string rather than the numeric `id`, so TanStack's rowSelection
     * map lines up with its `getRowId`. Selected transaction ids are still
     * deduplicated to the underlying numeric `id`.
     */
    getRowKey?: (row: T) => string;
}

export function useRuleTransactionSelection<T extends SelectableRow>({
    flattenedTransactions,
    isShowingApplied = false,
    matchingTotal,
    nonLockedIds,
    getRowKey: getRowKeyOverride,
}: UseRuleTransactionSelectionOptions<T>) {
    const getRowKey = useMemo(
        () => getRowKeyOverride ?? ((row: T) => String(row.id)),
        [getRowKeyOverride],
    );
    const [
        { value: excludedTransactionsIds },
        ,
        { setValue: setExcludedTransactions },
    ] = useField<number[]>("excludedTransactionsIds");
    const [
        { value: includedTransactionsIds },
        ,
        { setValue: setIncludedTransactions },
    ] = useField<number[]>("includedTransactionsIds");
    const [
        { value: applyToExistingTransactions },
        ,
        { setValue: setApplyToExistingTransactions },
    ] = useField<boolean>("applyToExistingTransactions");

    const selectableTransactions = useMemo(
        () => flattenedTransactions.filter((t) => nonLockedIds.has(t.id)),
        [flattenedTransactions, nonLockedIds],
    );

    const rowSelection = useMemo(() => {
        const selection: RowSelectionState = {};
        if (!applyToExistingTransactions || isShowingApplied) {
            return selection;
        }

        flattenedTransactions.forEach((transaction) => {
            const isSelected =
                includedTransactionsIds.length > 0
                    ? includedTransactionsIds.includes(transaction.id)
                    : !excludedTransactionsIds.includes(transaction.id);
            if (isSelected) {
                selection[getRowKey(transaction)] = true;
            }
        });

        return selection;
    }, [
        applyToExistingTransactions,
        excludedTransactionsIds,
        flattenedTransactions,
        getRowKey,
        includedTransactionsIds,
        isShowingApplied,
    ]);

    const clearSelectionState = useCallback(() => {
        setApplyToExistingTransactions(false);
        setIncludedTransactions([]);
        setExcludedTransactions([]);
    }, [
        setApplyToExistingTransactions,
        setExcludedTransactions,
        setIncludedTransactions,
    ]);

    const setAllTransactionsSelected = useCallback(() => {
        setApplyToExistingTransactions(true);
        setIncludedTransactions([]);
        setExcludedTransactions([]);
    }, [
        setApplyToExistingTransactions,
        setExcludedTransactions,
        setIncludedTransactions,
    ]);

    const setIncludedTransactionsSelection = useCallback(
        (selectedTransactionsIds: number[]) => {
            setApplyToExistingTransactions(true);
            setIncludedTransactions(selectedTransactionsIds);
            setExcludedTransactions([]);
        },
        [
            setApplyToExistingTransactions,
            setExcludedTransactions,
            setIncludedTransactions,
        ],
    );

    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            const nextSelection =
                typeof updater === "function" ? updater(rowSelection) : updater;

            // Multiple preview rows can share the same underlying transaction
            // id (e.g. split-rule previews fan one transaction into several
            // rows). We track include/exclude by numeric `id`, so we first
            // compute the per-parent-id target state from `nextSelection`,
            // propagating row-level toggles to their siblings via the diff
            // against the previous `rowSelection`.
            const parentState = new Map<number, boolean>();

            selectableTransactions.forEach((transaction) => {
                const key = getRowKey(transaction);
                const prevChecked = !!rowSelection[key];
                const nextChecked = !!nextSelection[key];

                if (prevChecked === nextChecked) {
                    // Row didn't change. Seed the parent's state from its
                    // existing value unless a sibling already claimed it.
                    if (!parentState.has(transaction.id)) {
                        parentState.set(transaction.id, prevChecked);
                    }
                    return;
                }

                // Row toggled — treat this as the user's intent for the whole
                // parent transaction, overriding any sibling rows.
                parentState.set(transaction.id, nextChecked);
            });

            const selectedIds = Array.from(parentState.entries())
                .filter(([, checked]) => checked)
                .map(([id]) => id);
            const excludedIds = Array.from(parentState.entries())
                .filter(([, checked]) => !checked)
                .map(([id]) => id);

            // Row toggles never escalate to all-matching — that is expressed
            // only via the header `selectAllControl`. This closes the
            // over-apply path where checking every loaded row silently applied
            // the rule to unseen pages too.
            const result = resolveRowSelection({
                selectedIds,
                excludedIds,
                applyToExistingTransactions,
                includedTransactionsIds,
            });

            switch (result.kind) {
                case "clear":
                    clearSelectionState();
                    return;
                case "included":
                    setIncludedTransactionsSelection(result.ids);
                    return;
                case "excluded":
                    setExcludedTransactions(result.ids);
                    setIncludedTransactions([]);
                    return;
            }
        },
        [
            applyToExistingTransactions,
            clearSelectionState,
            getRowKey,
            includedTransactionsIds,
            rowSelection,
            selectableTransactions,
            setExcludedTransactions,
            setIncludedTransactions,
            setIncludedTransactionsSelection,
        ],
    );

    const enableRowSelection = useCallback(
        (row: T) => !isShowingApplied && nonLockedIds.has(row.id),
        [isShowingApplied, nonLockedIds],
    );

    const selectedTransactionsCount = useMemo(() => {
        if (!applyToExistingTransactions) {
            return 0;
        }
        if (includedTransactionsIds.length > 0) {
            return includedTransactionsIds.length;
        }

        return Math.max(
            (matchingTotal ?? 0) - excludedTransactionsIds.length,
            0,
        );
    }, [
        applyToExistingTransactions,
        excludedTransactionsIds.length,
        includedTransactionsIds.length,
        matchingTotal,
    ]);

    const toggleRow = useCallback(
        (row: T) => {
            const rowKey = getRowKey(row);
            const nextSelection: RowSelectionState = { ...rowSelection };
            if (rowSelection[rowKey]) {
                delete nextSelection[rowKey];
            } else {
                nextSelection[rowKey] = true;
            }
            handleRowSelectionChange(nextSelection);
        },
        [getRowKey, handleRowSelectionChange, rowSelection],
    );

    // All-matching header control derived from selection state alone, so it is
    // correct regardless of how many pages are loaded. `undefined` (no known
    // match total, or applied view) makes the header fall back to page-row
    // behavior.
    const selectAllControl = useMemo<
        SelectAllMatchingControl | undefined
    >(() => {
        const state = resolveSelectAllControl({
            applyToExistingTransactions,
            includedTransactionsIds,
            excludedTransactionsIds,
            matchingTotal,
            isShowingApplied,
        });
        if (!state) {
            return undefined;
        }
        return {
            total: state.total,
            isChecked: state.isChecked,
            isIndeterminate: state.isIndeterminate,
            onToggle:
                state.action === "clear"
                    ? clearSelectionState
                    : setAllTransactionsSelected,
        };
    }, [
        applyToExistingTransactions,
        clearSelectionState,
        excludedTransactionsIds,
        includedTransactionsIds,
        isShowingApplied,
        matchingTotal,
        setAllTransactionsSelected,
    ]);

    return {
        applyToExistingTransactions,
        enableRowSelection,
        handleRowSelectionChange,
        rowSelection,
        selectedTransactionsCount,
        toggleRow,
        selectAllControl,
    };
}
